import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Switch,
  Paper,
  Button,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { mergeIntegrationService } from "services/mergeIntegration";
import { MergeDataProps } from "./ATSIntegrationSettings";

interface InterviewHelpATSConfigProps {
  expertId: string;
  mergeData: MergeDataProps | null;
  onSettingsUpdated?: () => void;
  setActiveATS?: (ats: string) => void;
  activeATS?: string;
}

const InterviewHelpATSConfig: React.FC<InterviewHelpATSConfigProps> = ({
  expertId,
  mergeData,
  onSettingsUpdated,
  setActiveATS,
  activeATS
}) => {
  const [syncEnabled, setSyncEnabled] = useState(mergeData?.enableSync ?? false);
  const [jobSyncEnabled, setJobSyncEnabled] = useState(mergeData?.enableJobSync ?? false);
  const [lastSyncTime, setLastSyncTime] = useState<string>("");
  const [configSaved, setConfigSaved] = useState(mergeData?.enableSync ?? false);
  const [loading, setLoading] = useState(false);
  const [dataSyncLoading, setDataSyncLoading] = useState(false);
  const [jobSyncLoading, setJobSyncLoading] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    type: "" as "success" | "error" | "info" | "",
    title: "",
    message: "",
  });

  useEffect(() => {
    if (mergeData?.lastUpdated) {
      const formatted = new Intl.DateTimeFormat("en-GB", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(mergeData.lastUpdated));
      setLastSyncTime(formatted);
    }
  }, [mergeData]);

  const showSnackbar = useCallback(
    (type: "success" | "error" | "info", title: string, message: string) => {
      setSnackbar({ open: true, type, title, message });
    },
    []
  );

  /** 🔹 Handle individual toggles */
  const handleToggle = useCallback(
    async (field: "sync" | "jobSync", newValue: boolean) => {
      if (!expertId) {
        showSnackbar("error", "Missing Expert ID", "Cannot update sync settings.");
        return;
      }

      // set loading only for the specific toggle
      if (field === "sync") setDataSyncLoading(true);
      else setJobSyncLoading(true);

      // optimistic UI update
      if (field === "sync") setSyncEnabled(newValue);
      else setJobSyncEnabled(newValue);

      try {
        await mergeIntegrationService.postSyncMergeSettings({
          expertId,
          enableSync: field === "sync" ? newValue : syncEnabled,
          enableJobSync: field === "jobSync" ? newValue : jobSyncEnabled,
        });

        const title =
          field === "sync" ? "Data Sync Updated" : "Job Sync Updated";
        const message = newValue
          ? `${field === "sync" ? "Data" : "Job"} synchronization enabled successfully.`
          : `${field === "sync" ? "Data" : "Job"} synchronization disabled.`;

        showSnackbar("success", title, message);

        if (newValue) {
          const now = new Intl.DateTimeFormat("en-GB", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date());
          setLastSyncTime(now);
        }
      } catch (error: any) {
        console.error("Error updating sync status:", error);
        const errMsg =
          error?.response?.data?.message ||
          `Failed to update ${field === "sync" ? "data" : "job"} sync setting.`;

        showSnackbar("error", "Update Failed", errMsg);

        // rollback optimistic change
        if (field === "sync") setSyncEnabled((prev) => !newValue);
        else setJobSyncEnabled((prev) => !newValue);
      } finally {
        // stop loading only for that specific toggle
        if (field === "sync") setDataSyncLoading(false);
        else setJobSyncLoading(false);
      }
    },
    [expertId, syncEnabled, jobSyncEnabled, showSnackbar]
  );

  /** 🔹 Save Config */
  const postInterviewHelpATSSettings = async (expertId: string) => {
    try {
      setLoading(true);
      await mergeIntegrationService.postMergeSetting({
        expertId,
        useDefaultMergeIntegration: true,
      });

      setConfigSaved(true);
      showSnackbar(
        "success",
        "Configuration Saved",
        "InterviewHelp ATS configuration saved successfully."
      );
      onSettingsUpdated?.();
    } catch (error: any) {
      console.error("Error saving InterviewHelp ATS settings:", error);
      const msg =
        error?.response?.data?.message ||
        "Failed to save InterviewHelp ATS configuration.";
      showSnackbar("error", "Error", msg);
    } finally {
      setLoading(false);
    }
  };

  /** 🔹 Delete Connection */
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await mergeIntegrationService.deleteMergeSetting({ expertId });
      setConfigSaved(false);
      setSyncEnabled(false);
      setJobSyncEnabled(false);
      showSnackbar(
        "info",
        "Connection Deleted",
        "ATS connection removed and synchronization disabled."
      );
      onSettingsUpdated?.();
      setActiveATS?.("");
    } catch (error: any) {
      console.error("Error deleting ATS connection:", error);
      const msg =
        error?.response?.data?.message ||
        "Failed to delete ATS connection. Please try again.";
      showSnackbar("error", "Error", msg);
    } finally {
      setLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  return (
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mt: 2 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            InterviewHelp ATS
          </Typography>
          <Typography color="text.secondary">
            Manage your InterviewHelp ATS synchronization settings
          </Typography>
        </Box>
        {configSaved && (
          <Chip
            icon={<CheckCircleIcon sx={{ color: "white !important" }} />}
            label="Active"
            color="success"
          />
        )}
      </Box>

      {/* 🔹 Sync Toggles */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        p={2}
        border="1px solid"
        borderColor="divider"
        borderRadius={2}
        mb={2}
        gap={2}
      >
        {/* Left: Enable Data Sync */}
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip
            title="Automatically synchronize candidate data between systems."
            arrow
            placement="top"
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ cursor: "help" }}
            >
              Enable Data Sync
            </Typography>
          </Tooltip>
          {dataSyncLoading && syncEnabled && (
            <CircularProgress size={16} color="primary" thickness={5} />
          )}
          <Switch
            checked={syncEnabled}
            onChange={(e) => handleToggle("sync", e.target.checked)}
            color="primary"
            disabled={!activeATS || dataSyncLoading}
          />
        </Box>

        {/* Right: Enable Job Sync */}
        <Box display="flex" alignItems="center" gap={1}>
          <Tooltip
            title="Automatically synchronize job data between systems."
            arrow
            placement="top"
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              sx={{ cursor: "help" }}
            >
              Enable Job Sync
            </Typography>
          </Tooltip>
          {jobSyncLoading && jobSyncEnabled && (
            <CircularProgress size={16} color="primary" thickness={5} />
          )}
          <Switch
            checked={jobSyncEnabled}
            onChange={(e) => handleToggle("jobSync", e.target.checked)}
            color="primary"
            disabled={!activeATS || jobSyncLoading}
          />
        </Box>
      </Box>

      {lastSyncTime && (
        <Typography color="text.secondary" mb={2}>
          Last synchronized: {lastSyncTime}
        </Typography>
      )}

      {/* Buttons */}
      {!configSaved ? (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
          startIcon={loading ? <CircularProgress size={18} /> : <SaveIcon />}
          onClick={() => postInterviewHelpATSSettings(expertId)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {loading ? "Saving..." : "Save Configuration"}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="error"
          fullWidth
          startIcon={<DeleteIcon />}
          disabled={loading}
          onClick={() => setOpenDeleteDialog(true)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          Delete ATS Connection
        </Button>
      )}

      {/* Delete Confirmation */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete ATS Connection?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will disconnect InterviewHelp ATS, stop all data
            synchronization, and remove saved configurations. This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            Delete Connection
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Snackbar Notifications - Top Right */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.type || "info"}
          variant="filled"
          sx={{ width: "100%" }}
        >
          <Typography fontWeight={600}>{snackbar.title}</Typography>
          <Typography fontSize={14}>{snackbar.message}</Typography>
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default InterviewHelpATSConfig;
